// Shared tunables and content data for the public landing page.
// Kept here (not scattered as magic literals) so card components and the
// snap-scroll deck reference a single source of truth.

// Niche-driven copy (hero eyebrow, ticker, ROI trades, signup trade select) is fetched live from the
// billing service's public niche-content catalog and passed into the cards as props. Plan content
// (name, tagline, features, price) is likewise fetched live from the billing service.

/** A labelled `<select>` option used by the landing trade and plan selects. */
export interface ILandingOption {
  key: string;
  label: string;
}

/** Landing-wide constants. */
export const LandingConstants = {
  // Deck cards.
  cardCount: 7,
  demoCardIndex: 1, // Card 2 (zero-based) — the live-demo call card.
  signupCardIndex: 6, // Card 7 (zero-based) — target of the header "Get Started" jump.
  roiCardIndex: 4, // Card 5 — target of the hero "Calculate Your Loss" jump.

  // Demo card visual tunables (Card 2). The waveform and tag-highlight are decorative; kept here so the card carries no magic literals.
  demoWaveformBars: 32, // Number of animated waveform bars in the demo panel.
  demoWaveformDelayStepMs: 80, // Per-bar animation-delay step, so the wave travels across the bars.
  demoTagDelayStepMs: 1100, // Per-pill slot duration; total toggle cycle = tags * step, so exactly one pill highlights at a time.

  // Hero eyebrow rotator.
  eyebrowIntervalMs: 2000,
  eyebrowAnimMs: 400,
  // One conveyor line in em — the slide distance. MUST match the .eyebrowRotator/.eyebrowWord height in
  // Landing.module.css. 1.6em leaves ink headroom for tall scripts (uppercase stacked diacritics), which
  // overflow a tight line box and bleed neighbouring words into the rotator window as edge artifacts.
  eyebrowLineEm: 1.6,

  // Hero headline fit-to-width. The headline lines are nowrap and splay left/right; long languages (Greek, German) overflow the column and slide under the floating hex cluster. A layout hook measures
  // each line plus its splay against the column width and lowers the `--hero-scale` CSS variable until it fits. The floor keeps the smallest scale legible rather than collapsing to nothing.
  heroHeadlineMinScale: 0.55,
  // A small slack (fraction of column width kept free) so the scaled headline never sits flush against the column edge or the hex cluster.
  heroHeadlineFitSlack: 0.04,

  // Hero footer ticker. The strip duplicates a single "group" of items and slides it left by one
  // group width, so the second copy seamlessly takes over with no visible reset. For that to read as
  // an infinite loop the group must be at least as wide as the viewport; with only a few short items
  // (e.g. when the niche catalog has not loaded) one copy is narrower than the screen and the strip
  // visibly snaps back. So the group repeats the base item list enough times to overflow the viewport.
  tickerMinGroupRepeats: 2, // Floor on base-list copies per group (covers the empty/degraded case).
  tickerOverflowRepeats: 1, // Extra base-list copies beyond the measured fill so the group always overflows.

  // ROI calculator model (mirrors the marketing reference math). Per-niche average job values come
  // from the niche-content catalog; these tunables shape the rest of the estimate.
  roiWeeksPerMonth: 4.3,
  // Echo monthly cost shown in the ROI breakdown before the live plan catalog loads (dollars). The card uses the featured plan's price once fetched.
  roiEchoMonthlyCostFallback: 597,
  roiCallsMin: 1,
  roiCallsMax: 30,
  roiCallsDefault: 5,
  roiCloseMin: 10,
  roiCloseMax: 90,
  roiCloseDefault: 40,
  // Exclusive lower bound for the editable average-job-value field: any value at or below this (or a non-number) is invalid and falls back to the trade's niche default.
  roiJobValueMin: 0,
  // Inclusive upper bound for the editable average-job-value field (6 digits). Keeps the math sane and stops a long number from stretching the card layout; a per-job average never realistically
  // exceeds this. Entries above it are clamped down to it.
  roiJobValueMax: 999999,
  percentDivisor: 100,
  // Divisor used to compute the midpoint tick shown between a slider's min and max labels.
  midpointDivisor: 2,
  monthsPerYear: 12,

  // Logo display size (height in rem; width auto-scales to retain aspect).
  logoHeightRem: 1.75
} as const;
